import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import {
  DESTINATION_LOCATION_TYPE,
  MODE_SELECTOR,
  ORIGIN_LOCATION_TYPE,
} from '#/shared/constants';
import { getDepartureOptionData } from '#/trip-planner/utils/date-time';
import GTM_HANDLES from '#/shared/gtm-handles';
import { locationType } from '#/prop-types';
import useModeLabel from '#/trip-planner/hooks/useModeLabel';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import * as Styled from './styled';
import EndpointField from './EndpointField';

function TripRequestForm({
  origin,
  destination,
  currentLocation,
  planTrip,
  departureOption,
  tripTimestamp,
  swapEndpoints,
  onOriginFieldClick,
  onDestinationFieldClick,
  onModeFieldClick,
  onDateTimeFieldClick,
}) {
  const { departNow, intlId: departureOptionIntlId } =
    getDepartureOptionData(departureOption);

  const tripDatetime = new Date(tripTimestamp);
  const { formatToTriMetDateString, formatToTriMetTimeString } =
    useIntlDatetime();

  const { getLabelFromModeString } = useModeLabel();
  const { currentQuery } = useCurrentQuery();
  const tripMode = getLabelFromModeString(currentQuery[MODE_SELECTOR]);

  return (
    <Styled.TripRequestWrapper>
      <Styled.TripRequestBody>
        <Styled.TripRequestEndpoints>
          <Styled.TripRequestRowColumnGrow>
            <Styled.TripRequestRow firstRow>
              <EndpointField
                location={origin}
                locationType={ORIGIN_LOCATION_TYPE}
                userLocation={currentLocation}
                onClick={onOriginFieldClick}
                gtmHandle={GTM_HANDLES.tripRequest.endpointFrom}
                labelIntlId="planner.from"
                accessibilityIntlId="planner.changeorigin_acc"
                emptyInputIntlId="planner.wherefrom"
              />
            </Styled.TripRequestRow>
            <Styled.TripRequestRow>
              <EndpointField
                location={destination}
                locationType={DESTINATION_LOCATION_TYPE}
                userLocation={currentLocation}
                onClick={onDestinationFieldClick}
                gtmHandle={GTM_HANDLES.tripRequest.endpointTo}
                labelIntlId="planner.to"
                accessibilityIntlId="planner.changedestination_acc"
                emptyInputIntlId="planner.whereto"
              />
            </Styled.TripRequestRow>
          </Styled.TripRequestRowColumnGrow>
          <Styled.TripRequestRowColumnShrink>
            <Styled.SwapEndpointButton
              onClick={swapEndpoints}
              disabled={!origin && !destination}
              data-testid="swapEndpointsButton"
              dataGtm={GTM_HANDLES.tripRequest.swapEndpointsButton}
            >
              <Styled.SwapIcon />
              <ScreenReaderText>
                <FormattedMessage id="planner.swap_acc" />
              </ScreenReaderText>
            </Styled.SwapEndpointButton>
          </Styled.TripRequestRowColumnShrink>
        </Styled.TripRequestEndpoints>

        <Styled.TripRequestRow>
          <Styled.TripRequestRowColumnShrink>
            <Styled.CalendarIcon />
          </Styled.TripRequestRowColumnShrink>
          <Styled.TripRequestRowColumnGrow>
            <Styled.TripOptionsLabel>
              <FormattedMessage id="planner.when" />
            </Styled.TripOptionsLabel>
            <Styled.SettingsButton
              onClick={onDateTimeFieldClick}
              dataGtm={GTM_HANDLES.tripRequest.dateTimeButton}
            >
              <Styled.SettingsButtonContentColumn>
                <Styled.SettingsButtonPrimaryText>
                  <FormattedMessage id={departureOptionIntlId} />
                  {!departNow && (
                    <>
                      {' '}
                      {formatToTriMetTimeString(tripDatetime)}
                      <br />
                      {formatToTriMetDateString(tripDatetime)}
                    </>
                  )}
                </Styled.SettingsButtonPrimaryText>
                <Styled.SettingsButtonSecondaryText>
                  <FormattedMessage id="planner.changetime" />
                </Styled.SettingsButtonSecondaryText>
              </Styled.SettingsButtonContentColumn>
              <Styled.SettingsButtonIconColumn>
                <FormattedMessage id="planner.edit" />
                <Styled.EditIcon />
              </Styled.SettingsButtonIconColumn>
            </Styled.SettingsButton>
          </Styled.TripRequestRowColumnGrow>
        </Styled.TripRequestRow>

        <Styled.TripRequestRow>
          <Styled.TripRequestRowColumnShrink>
            <Styled.CogIcon />
          </Styled.TripRequestRowColumnShrink>
          <Styled.TripRequestRowColumnGrow>
            <Styled.TripOptionsLabel>
              <FormattedMessage id="planner.traveloptions" />
            </Styled.TripOptionsLabel>
            <Styled.SettingsButton
              onClick={onModeFieldClick}
              dataGtm={GTM_HANDLES.tripRequest.travelOptionsButton}
            >
              <Styled.SettingsButtonContentColumn>
                <Styled.SettingsButtonPrimaryText>
                  {tripMode}
                </Styled.SettingsButtonPrimaryText>
                <Styled.SettingsButtonSecondaryText>
                  <FormattedMessage id="planner.changeoptions" />
                </Styled.SettingsButtonSecondaryText>
              </Styled.SettingsButtonContentColumn>
              <Styled.SettingsButtonIconColumn>
                <FormattedMessage id="planner.edit" />
                <Styled.EditIcon />
              </Styled.SettingsButtonIconColumn>
            </Styled.SettingsButton>
          </Styled.TripRequestRowColumnGrow>
        </Styled.TripRequestRow>

        <Styled.TripRequestFooter>
          <Styled.PlanTripButton
            onClick={planTrip}
            active={!!origin && !!destination}
            disabled={!origin || !destination}
            dataGtm={GTM_HANDLES.tripRequest.planTripButton}
            variant="primary"
            data-testid="planTripButton"
          >
            <FormattedMessage id="planner.plantrip" />
          </Styled.PlanTripButton>
        </Styled.TripRequestFooter>
        <Styled.TripRequestSpacer />
      </Styled.TripRequestBody>
    </Styled.TripRequestWrapper>
  );
}

TripRequestForm.propTypes = {
  /** Location designated as origin for a trip plan */
  origin: locationType,
  /** Location designated as destination for a trip plan */
  destination: locationType,
  /** The user's current location */
  currentLocation: locationType,
  /** Departure type for trip, e.g. depart now, arrive by, etc. */
  departureOption: PropTypes.string.isRequired,
  /** Selected datetime for trip in ms since epoch */
  tripTimestamp: PropTypes.number.isRequired,
  /** Submits the trip request */
  planTrip: PropTypes.func.isRequired,
  /** Swaps the origin and destination locations  */
  swapEndpoints: PropTypes.func.isRequired,
  /** Handler for click on origin field */
  onOriginFieldClick: PropTypes.func.isRequired,
  /** Handler for click on destination field */
  onDestinationFieldClick: PropTypes.func.isRequired,
  /** Handler for click on trip mode field */
  onModeFieldClick: PropTypes.func.isRequired,
  /** Handler for click on datetime field */
  onDateTimeFieldClick: PropTypes.func.isRequired,
};

TripRequestForm.defaultProps = {
  origin: null,
  destination: null,
  currentLocation: null,
};

export default TripRequestForm;
